export const CAMERA_OPTIONS = [
  { value: 'environment', label: 'Cámara posterior' },
  { value: 'user', label: 'Cámara frontal' },
];

export const DEFAULT_CAMERA = 'environment';

export class CameraAccessError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'CameraAccessError';
    this.code = code;
  }
}

export async function getCameraPermissionState() {
  if (!navigator.permissions?.query) return 'unknown';
  try {
    const result = await navigator.permissions.query({ name: 'camera' });
    return result.state;
  } catch {
    return 'unknown';
  }
}

export async function requestCameraStream(facingMode = DEFAULT_CAMERA) {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new CameraAccessError(
      'Este navegador no permite acceder a la cámara. Usá un navegador actualizado con HTTPS.',
      'unsupported',
    );
  }

  const permission = await getCameraPermissionState();
  if (permission === 'denied') {
    throw new CameraAccessError(
      'El acceso a la cámara está bloqueado. Habilitalo desde la configuración del navegador o dispositivo y volvé a intentar.',
      'denied',
    );
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: facingMode },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });
  } catch (error) {
    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      throw new CameraAccessError(
        'No se autorizó el acceso a la cámara. Habilitalo desde la configuración del navegador o dispositivo.',
        'denied',
      );
    }
    if (error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError') {
      throw new CameraAccessError(
        `No se encontró la ${facingMode === 'environment' ? 'cámara posterior' : 'cámara frontal'} en este dispositivo.`,
        'not-found',
      );
    }
    throw new CameraAccessError('No se pudo iniciar la cámara. Cerrá otras aplicaciones que puedan estar usándola.', 'unavailable');
  }
}

export function stopCameraStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

/**
 * Mobile plate capture: rear camera + highest practical resolution (1080p ideal).
 */
export async function requestMobileCameraStream(facingMode = DEFAULT_CAMERA) {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new CameraAccessError(
      'Este navegador no permite acceder a la cámara. Usá un navegador actualizado con HTTPS.',
      'unsupported',
    );
  }

  const permission = await getCameraPermissionState();
  if (permission === 'denied') {
    throw new CameraAccessError(
      'El acceso a la cámara está bloqueado. Habilitalo desde la configuración del navegador o dispositivo y volvé a intentar.',
      'denied',
    );
  }

  const highRes = {
    audio: false,
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: 1920, min: 640 },
      height: { ideal: 1080, min: 480 },
    },
  };

  try {
    return await navigator.mediaDevices.getUserMedia(highRes);
  } catch (error) {
    if (error?.name === 'OverconstrainedError' || error?.name === 'NotFoundError') {
      return requestCameraStream(facingMode);
    }
    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      throw new CameraAccessError(
        'No se autorizó el acceso a la cámara. Habilitalo desde la configuración del navegador o dispositivo.',
        'denied',
      );
    }
    throw new CameraAccessError('No se pudo iniciar la cámara. Cerrá otras aplicaciones que puedan estar usándola.', 'unavailable');
  }
}
